import {
  QConfigRandomType,
  QConfigTimeLimitType,
  type GetQConfigByIdQuery,
} from '@exam-management/graphql';
import { z } from 'zod';

// NOTE: We can remove this if TimeInput is upgraded to version 7 (currently 5), because it's support min/max props
// Ref: https://mantine.dev/dates/time-input/?t=props
// If TimeInput is already upgraded, please remove these const and min validation in here
const MIN_EXAM_TIMING_DURATION = 60;
const MAX_EXAM_TIMING_DURATION = 86340; // 23:59

type QConfigStructuresType = GetQConfigByIdQuery['excGetQConfigById']['qConfig']['structures'];

const PassingCriteriaSchema = z
  .union([
    z.string().transform((val) => {
      if (val === '' || Number.isNaN(parseInt(val))) return null;
      return parseInt(val);
    }),
    z.number().min(0).max(100),
  ])
  .nullish();

export const QConfigSettingsFormSchema = z.object({
  passingCriteria: z.object({
    overall: PassingCriteriaSchema,
    structures: z.record(PassingCriteriaSchema).nullish(),
  }),
  examTiming: z
    .object({
      option: z.enum([QConfigTimeLimitType.All, QConfigTimeLimitType.ByStructure]).nullish(),
      overall: z
        .date()
        .nullish()
        .refine(
          (date) => {
            if (!date) {
              return true;
            }
            const dateInput = date;
            const duration = dateInput.getHours() * 3600 + dateInput.getMinutes() * 60;
            return duration >= MIN_EXAM_TIMING_DURATION;
          },
          { message: 'ระยะเวลารวมของการสอบ ต้องมากกว่า 0' },
        ),
      structures: z
        .array(
          z.object({
            id: z.string(),
            parts: z
              .array(
                z.object({
                  label: z.string(),
                  value: z.string(),
                }),
              )
              .min(1, { message: 'กรุณาเลือกหมวดที่ต้องการ' }),
            timeLimit: z
              .date()
              .nullable()
              .refine((date) => date, { message: 'กรุณากำหนดระยะเวลาสอบ' }) // we need this because we need to add new timeLimit with null value at first
              .refine(
                (date) => {
                  if (!date) {
                    return true;
                  }
                  const duration = date.getHours() * 3600 + date.getMinutes() * 60;
                  return duration >= MIN_EXAM_TIMING_DURATION;
                },
                { message: 'ระยะเวลาของการสอบ ต้องมากกว่า 0' },
              ),
          }),
        )
        .superRefine((structures, ctx) => {
          const sumTimeLimit = structures.reduce((acc, curr) => {
            return (
              acc +
              (curr.timeLimit?.getHours() ?? 0) * 3600 +
              (curr.timeLimit?.getMinutes() ?? 0) * 60
            );
          }, 0);

          if (sumTimeLimit > MAX_EXAM_TIMING_DURATION) {
            ctx.addIssue({
              code: 'custom',
              message: 'ระยะเวลาสอบในแต่ละช่วงรวมกันต้องไม่เกิน 24 ชั่วโมง กรุณาปรับค่าใหม่',
              path: ['root'],
            });
            for (let i = 0; i < (structures.length ?? 0); i++) {
              ctx.addIssue({
                code: 'custom',
                message: '',
                path: [`${i}.timeLimit`],
              });
            }
          }
        }),
    })
    .nullish()
    .refine(
      (examTimingData) =>
        !examTimingData ||
        examTimingData.option !== QConfigTimeLimitType.All ||
        examTimingData.overall,
      {
        message: 'กรุณากรอกระยะเวลาทำข้อสอบ',
        path: ['overall'],
      },
    ),
  randomization: z
    .enum([
      QConfigRandomType.AllRandom,
      QConfigRandomType.Fixed,
      QConfigRandomType.RandomByHierarchy,
    ])
    .nullish(),
});

export function getQConfigSettingsFormSchema(fullScore: number, structures: QConfigStructuresType) {
  return QConfigSettingsFormSchema.superRefine((data, ctx) => {
    const currentStructures = data.passingCriteria.structures ?? {};

    const structurePassingScore = Object.keys(currentStructures).reduce((acc, key) => {
      const structurePercentage = (currentStructures[key] ?? 0) / 100;
      const structureFullScore = structures.find((structure) => structure.id === key)?.score ?? 0;
      return acc + structureFullScore * structurePercentage;
    }, 0);
    const overallPassingScore = fullScore * ((data.passingCriteria.overall ?? 0) / 100);

    const isExceeded = structurePassingScore > overallPassingScore;
    if (isExceeded) {
      ctx.addIssue({
        code: 'custom',
        message:
          'คะแนนผ่านในแต่ละหมวดรวมกันต้องมีค่าไม่เกินคะแนนผ่านรวมที่กำหนดไว้ กรุณาปรับค่าใหม่',
        // NOTE: There's an issue with react-hook-form
        // It'll return a message with root field in some events, and no root field in some events (ex. formContext.trigger)
        // More info: https://github.com/react-hook-form/resolvers/issues/561
        path: ['passingCriteria.structures.root'],
      });
      for (const key in currentStructures) {
        ctx.addIssue({
          code: 'custom',
          message: '',
          path: [`passingCriteria.structures.${key}`],
        });
      }
    }
  });
}

export type QConfigSettingsFormInput = z.infer<typeof QConfigSettingsFormSchema>;
